<script>
  import { onMount } from 'svelte';
  import Chart from 'chart.js/auto';
  import 'chartjs-adapter-date-fns';
  import { theme } from '$stores/themeStore';
  import { studyProperties } from '$stores/configStore';
  
  export let readings = [];
  export let date = '';

  $: formattedDate = date ? new Date(date.replace(/-/g, '/')).toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric'
  }) : '';

  let canvas;
  let chart;

  $: if (canvas && readings) {
    drawChart();
  }

  $: if (canvas && chart && $theme) {
    // Redraw chart when theme changes to update colors
    drawChart();
  }

  function drawChart() {
    if (chart) {
      chart.destroy();
    }

    const readingsArray = Array.isArray(readings) ? readings : [];
    readingsArray.sort((a, b) => new Date(a.read) - new Date(b.read));

    const firstReadingDate = readingsArray[0]?.read ? new Date(readingsArray[0].read) : new Date(date.replace(/-/g, '/'));
    const startOfDay = new Date(firstReadingDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(firstReadingDate);
    endOfDay.setHours(23, 59, 59, 999);

    // Create data points with backfill information
    const dataPoints = [];
    // Determine gap threshold based on CGM interval: break the line when gap exceeds 2 * interval
    const intervalMinutes = $studyProperties?.glucoseParameters?.cgmIntervalMinutes ?? 5;
    const gapThresholdMs = Math.max(1, intervalMinutes * 2) * 60 * 1000;

    readingsArray.forEach((reading, index) => {
      const currentTime = new Date(reading.read);
      const nextReading = readingsArray[index + 1];
      const nextTime = nextReading ? new Date(nextReading.read) : null;

      // Check if reading is backfilled
      const isBackfilled = reading.raw && 
        (reading.raw.includes('"isBackfilled":true') || 
         reading.raw.includes('"wasBackfilled":true'));

      //console.log(reading);

      // Add current point with backfill info
      dataPoints.push({
        x: currentTime,
        y: 1,
        isBackfilled
      });

      // If there's a gap larger than the threshold, add null points to break the line
      if (nextTime && (nextTime - currentTime) > gapThresholdMs) {
        dataPoints.push({
          x: currentTime,
          y: null,
          isBackfilled
        });
        dataPoints.push({
          x: nextTime,
          y: null,
          isBackfilled: nextReading.raw && 
            (nextReading.raw.includes('"isBackfilled":true') || 
             nextReading.raw.includes('"wasBackfilled":true'))
        });
      }
    });

    const isDarkMode = $theme === 'dark';
    const textColor = isDarkMode ? 'white' : 'black';

    chart = new Chart(canvas, {
      type: 'line',
      data: {
        datasets: [{
          data: dataPoints,
          borderWidth: 10,
          pointRadius: 0,
          fill: true,
          segment: {
            borderColor: ctx => {
              if (ctx.p0.y === null || ctx.p1.y === null) return 'transparent';
              // Check if either point in the segment is backfilled
              const isSegmentBackfilled = ctx.p0.raw.isBackfilled || ctx.p1.raw.isBackfilled;
              return isSegmentBackfilled ? 
                'rgba(50, 130, 170, 0.75)' :  // backfilled
                'rgba(60, 170, 170, 0.75)';   // regular
            },
            backgroundColor: ctx => {
              if (ctx.p0.y === null || ctx.p1.y === null) return 'transparent';
              const isSegmentBackfilled = ctx.p0.raw.isBackfilled || ctx.p1.raw.isBackfilled;
              return isSegmentBackfilled ? 
                'rgba(205, 159, 64, 0)' :
                'rgba(75, 192, 192, 0)';
            }
          }
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            enabled: false
          },
          legend: {
            display: false
          },
          title: {
            display: true,
            text: formattedDate,
            color: textColor,
            position: 'left',
            font: {
              size: 8,
              weight: 'light'
            }
          }
        },
        scales: {
          x: {
            type: 'time',
            min: startOfDay,
            max: endOfDay,
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH'
              }
            },
            ticks: {
              display: false
            },
            grid: {
              display: false
            },
            border: {
              display: false
            }
          },
          y: {
            display: false,
            min: 0.25,
            max: 2.0,
            grid: {
              display: false
            }
          }
        }
      }
    });
  }

  onMount(() => {
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  });
</script>

<div class="daily-compliance">
  <canvas
    bind:this={canvas}
    class="bg-white dark:bg-gray-800/50 rounded shadow-sm"
  />
</div>

<style>
  .daily-compliance {
    display: block;
    margin: 2px 0px 2px 4px;
    width: calc(100% - 10px);
    min-width: 266px;
    height: 45px;
  }
</style>